import className from 'classnames/bind';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { VideoPlaylistFragment } from '@graphqlTypes';
import { Image, VideoEmbed, VideoEmbedNoPreview, YoutubeThumbnail } from '@components';
import ArrowRightIcon from '@/assets/svg/arrow-right.svg';
import { removeHtmlTags, replaceStringVar } from '@/core/utils';

import styles from './VideoPlaylistDesktop.module.scss';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
const cx = className.bind(styles);

type EmbedRef = {
  getPlayer: () => YT.Player | null;
  showEmbed?: () => void;
}

type Props = Omit<VideoPlaylistFragment, '__typename' | 'primaryVideo' | 'videos'> & {
  primaryVideo?: Omit<VideoPlaylistFragment['primaryVideo'], '__typename'>;
  videos?: Omit<VideoPlaylistFragment['videos'][number], '__typename'>[];
  largeTitle?: boolean;
};

export default function VideoPlaylistDesktop(props: Props) {
    const { primaryVideo, videos } = props;
    const playerRefs = useRef<EmbedRef[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const previousIndex = useRef(0);
    const isFirstRender = useRef(true);
    const { labels: { ariaLabels } } = usePageWrapperData();
    const getVideoThumbnail = (video: typeof primaryVideo | typeof videos[number]) => {
      if (video?.videoType?.[0] === 'youtube') return video.youtubeThumbnail;
      else if (video?.videoThumbnail) return video.videoThumbnail;
      return null;
    }

    const videoThumbnails = useMemo(() => {
      return [primaryVideo, ...(videos || [])].map(video => ({
        ...video,
        videoThumb: getVideoThumbnail(video),
      }));
    }, [primaryVideo, videos]);

    const setRef = (index: number, ref: EmbedRef | null) => {
      if (ref) playerRefs.current[index] = ref;
    };

    useEffect(() => {
      const prevPlayer = playerRefs.current[previousIndex.current]?.getPlayer();
      if (prevPlayer) prevPlayer.pauseVideo();
      const activePlayer = playerRefs.current[activeIndex]?.getPlayer();
      if (activePlayer && !isFirstRender.current) {
        activePlayer.playVideo();
        playerRefs.current[activeIndex]?.showEmbed?.();
      } else if (activeIndex === 0 && previousIndex.current !== 0) {
        playerRefs.current[activeIndex]?.showEmbed?.();
      }
      isFirstRender.current = false;
      previousIndex.current = activeIndex;
    }, [activeIndex]);
  return (
    <div className={cx("video-playlist-desktop")}>
      <div className={cx("video-container")}>
        <div className={cx("video-items", "icon-button-hover-container")}>
          <VideoEmbed
            className={cx(["video", { 'active': activeIndex === 0 }])}
            {...primaryVideo}
            ref={setRef.bind(null, 0)}
          />
          {activeIndex === 0 && (
            <div className={cx("video-items-title", "title-lc-s")} dangerouslySetInnerHTML={{ __html: primaryVideo.performanceName }} />
          )}
          {videos?.map((video, index) => (
            <Fragment key={`${video.youtubeVideoId}-${index}`}>
              <VideoEmbedNoPreview
                className={cx(["video", { 'active': activeIndex === index + 1 }])}
                {...video}
                autoplay={false}
                ref={setRef.bind(null, index + 1)}
              />
              {activeIndex === index + 1 && (
                <div className={cx("video-items-title", "title-lc-s")} dangerouslySetInnerHTML={{ __html: video.performanceName }} />
              )}
            </Fragment>
          ))}
        </div>

        <div className={cx("video-thumbnails")}>
          {videoThumbnails?.map((video, index) => (
            <button key={index} className={cx("video-thumbnail", { 'active': activeIndex === index })} onClick={() => setActiveIndex(index)} aria-label={replaceStringVar(ariaLabels?.playVideo, { name: removeHtmlTags(video.performanceName) })}>
              <div className={cx("image-wrapper")}>
                <ArrowRightIcon className={cx("icon")} />
                { video.videoThumb
                  ? <Image image={video.videoThumb} />
                  : <YoutubeThumbnail youtubeId={video.youtubeVideoId} alt={replaceStringVar(ariaLabels?.playThumbnailAlt, { name: removeHtmlTags(video.performanceName) })} />
                }
              </div>
              <div className={cx(["copy", "text-s"])} dangerouslySetInnerHTML={{ __html: video.performanceName }} />
            </button>
          ))}
        </div>

      </div>
    </div>
  )
}